import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import DOMPurify from 'dompurify';
import { sniffImageMimeType } from '../releases/releases.component';
import {
  ContainerInfo,
  IotEdgeReleaseResponse,
  IotEdgeReleaseService,
  ReleaseChangelog,
  ReleaseTrigger,
  SiteReleasesResponse,
} from '../services/iotEdgeRelease.service';

@Component({
  selector: 'app-sitedetails-releases',
  templateUrl: './sitedetails-releases.component.html',
  styleUrls: ['./sitedetails-releases.component.css'],
  standalone: false,
})
export class SitedetailsReleasesComponent implements OnInit {
  constructor(
    private releaseService: IotEdgeReleaseService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public snackBar: MatSnackBar,
    private sanitizer: DomSanitizer
  ) {}

  private siteId: string;

  allReleases: IotEdgeReleaseResponse[] = [];
  siteReleases: SiteReleasesResponse | null = null;

  selectedDesiredReleaseId: string | null = null;
  selectedNewestAvailableReleaseId: string | null = null;

  saving = false;
  savingAvailable = false;
  syncing = false;
  expandedChangelogReleaseId: string | null = null;

  ngOnInit(): void {
    this.siteId = this.route.snapshot.paramMap.get('siteId');
    this.loadData();
  }

  loadData() {
    this.releaseService.getAllReleases().subscribe(
      (releases) => {
        this.allReleases = releases.sort((a, b) => this.compareVersionsDesc(a.version, b.version));
      },
      (err) => {
        console.error(err);
        this.snackBar.open('Error loading releases: ' + err.message, null, { duration: 8000 });
      }
    );

    this.releaseService.getSiteReleases(this.siteId).subscribe(
      (siteReleases) => {
        this.siteReleases = siteReleases;
        this.selectedDesiredReleaseId = siteReleases.desiredRelease?.id || null;
        this.selectedNewestAvailableReleaseId = this.resolveNewestAvailableId(siteReleases.availableReleases);
      },
      (err) => {
        console.error(err);
        this.snackBar.open('Error loading site releases: ' + err.message, null, { duration: 8000 });
      }
    );
  }

  get latestRelease(): IotEdgeReleaseResponse | null {
    return this.allReleases.length > 0 ? this.allReleases[0] : null;
  }

  get latestAvailableRelease(): IotEdgeReleaseResponse | null {
    if (!this.selectedNewestAvailableReleaseId) return null;
    return this.allReleases.find((r) => r.id === this.selectedNewestAvailableReleaseId) || null;
  }

  get isOnLatest(): boolean {
    if (!this.siteReleases?.currentRelease || !this.latestAvailableRelease) return false;
    return this.siteReleases.currentRelease.id === this.latestAvailableRelease.id;
  }

  /** Whether the globally latest release is in this site's available list */
  get isLatestAvailableForThisSite(): boolean {
    if (!this.siteReleases || !this.latestRelease) return false;
    return this.siteReleases.availableReleases.some((r) => r.id === this.latestRelease.id);
  }

  /** true = ready to install; false = available but not yet ready; null = not available */
  get isLatestAvailableReady(): boolean | null {
    if (!this.latestAvailableRelease) return null;
    return (this.latestAvailableRelease.readySiteIds ?? []).includes(this.siteId);
  }

  syncReleases() {
    this.syncing = true;
    this.releaseService.syncReleases().subscribe(
      () => {
        this.snackBar.open('Releases synced from IoT Hub', null, { duration: 5000 });
        this.syncing = false;
        this.loadData();
      },
      (err) => {
        console.error(err);
        this.snackBar.open('Error syncing releases: ' + err.message, null, { duration: 8000 });
        this.syncing = false;
      }
    );
  }

  saveDesiredRelease() {
    if (!this.selectedDesiredReleaseId) {
      this.snackBar.open('Please select a desired release', null, { duration: 5000 });
      return;
    }
    this.saving = true;
    this.releaseService.setDesiredRelease(this.siteId, this.selectedDesiredReleaseId).subscribe(
      (response) => {
        this.snackBar.open('Desired release saved', null, { duration: 5000 });
        this.saving = false;
        this.siteReleases = response;
        this.selectedDesiredReleaseId = response.desiredRelease?.id || null;
      },
      (err) => {
        console.error(err);
        this.snackBar.open('Error saving desired release: ' + (err.error?.message || err.message), null, {
          duration: 8000,
        });
        this.saving = false;
      }
    );
  }

  get currentNewestAvailableVersion(): string | null {
    if (!this.selectedNewestAvailableReleaseId) return null;
    return this.allReleases.find((r) => r.id === this.selectedNewestAvailableReleaseId)?.version || null;
  }

  openAvailableReleasesDialog(): void {
    const dialogRef = this.dialog.open(SetAvailableReleaseDialogComponent, {
      width: '500px',
      data: {
        allReleases: this.allReleases,
        currentNewestId: this.selectedNewestAvailableReleaseId,
      },
    });
    dialogRef.afterClosed().subscribe((result?: { newestId: string | null }) => {
      if (result === undefined) return;
      this.selectedNewestAvailableReleaseId = result.newestId;
      this.saveAvailableReleases();
    });
  }

  saveAvailableReleases() {
    this.savingAvailable = true;
    const selectedIndex = this.allReleases.findIndex((r) => r.id === this.selectedNewestAvailableReleaseId);
    // Include the selected release and all older ones (allReleases is sorted newest-first)
    const ids = selectedIndex >= 0 ? this.allReleases.slice(selectedIndex).map((r) => r.id) : [];
    this.releaseService.setAvailableReleases(this.siteId, ids).subscribe(
      (response) => {
        this.snackBar.open('Available releases saved', null, { duration: 5000 });
        this.savingAvailable = false;
        this.siteReleases = response;
        this.selectedNewestAvailableReleaseId = this.resolveNewestAvailableId(response.availableReleases);
      },
      (err) => {
        console.error(err);
        this.snackBar.open('Error saving available releases: ' + (err.error?.message || err.message), null, {
          duration: 8000,
        });
        this.savingAvailable = false;
      }
    );
  }

  private resolveNewestAvailableId(available: IotEdgeReleaseResponse[]): string | null {
    if (!available || available.length === 0) return null;
    // allReleases is sorted newest-first; pick whichever available release appears earliest
    for (const r of this.allReleases) {
      if (available.some((a) => a.id === r.id)) return r.id;
    }
    // fallback: pick by lastUpdatedAt
    return available.reduce((a, b) => (new Date(a.lastUpdatedAt) > new Date(b.lastUpdatedAt) ? a : b)).id;
  }

  get availableSelectionCount(): number {
    if (!this.selectedNewestAvailableReleaseId) return 0;
    const idx = this.allReleases.findIndex((r) => r.id === this.selectedNewestAvailableReleaseId);
    return idx >= 0 ? this.allReleases.length - idx : 0;
  }

  private compareVersionsDesc(a: string, b: string): number {
    const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
    const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const diff = (pb[i] || 0) - (pa[i] || 0);
      if (diff !== 0) return diff;
    }
    return 0;
  }

  toggleChangelog(releaseId: string) {
    this.expandedChangelogReleaseId = this.expandedChangelogReleaseId === releaseId ? null : releaseId;
  }

  get expandedChangelogRelease(): IotEdgeReleaseResponse | null {
    if (!this.expandedChangelogReleaseId) return null;
    return this.allReleases.find((r) => r.id === this.expandedChangelogReleaseId) || null;
  }

  /**
   * Sanitizes the HTML with DOMPurify (removes scripts/event handlers while keeping
   * inline styles and data: image URIs used by changelogs), then marks the cleaned
   * result as trusted so Angular renders it as-is.
   */
  getSafeHtml(html: string): SafeHtml {
    const clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }

  getChangelogImageUrl(changelog: ReleaseChangelog | null): string | null {
    return changelog?.image ? `data:${sniffImageMimeType(changelog.image)};base64,${changelog.image}` : null;
  }

  getReleaseLabel(release: IotEdgeReleaseResponse): string {
    if (release.releaseDate) {
      const d = new Date(release.releaseDate);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${release.version} — ${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    }
    return release.version;
  }

  get releaseTriggerLabel(): string | null {
    switch (this.siteReleases?.releaseTrigger) {
      case ReleaseTrigger.Desired:
        return 'Desired';
      case ReleaseTrigger.LatestAvailable:
        return 'Latest available';
      default:
        return null;
    }
  }

  get releaseTriggerBadgeClass(): string {
    return this.siteReleases?.releaseTrigger === ReleaseTrigger.Desired ? 'desired' : 'latest';
  }

  isCurrentRelease(releaseId: string): boolean {
    return this.siteReleases?.currentRelease?.id === releaseId;
  }

  isDesiredRelease(releaseId: string): boolean {
    return this.siteReleases?.desiredRelease?.id === releaseId;
  }

  isReleaseAvailableForSite(releaseId: string): boolean {
    return this.siteReleases?.availableReleases?.some((r) => r.id === releaseId) ?? false;
  }

  isReleaseReadyForSite(release: IotEdgeReleaseResponse): boolean {
    return (release.readySiteIds ?? []).includes(this.siteId);
  }

  get siteAvailableReleases(): IotEdgeReleaseResponse[] {
    if (!this.siteReleases?.availableReleases?.length) return [];
    const availableIds = new Set(this.siteReleases.availableReleases.map((r) => r.id));
    return this.allReleases.filter((r) => availableIds.has(r.id));
  }

  /**
   * Returns true when this container's version differs from the same container
   * in the immediately older release (siteAvailableReleases is sorted newest-first,
   * so the previous release is at releaseIndex + 1).
   */
  isContainerChanged(releaseIndex: number, container: ContainerInfo): boolean {
    const prevRelease = this.siteAvailableReleases[releaseIndex + 1];
    if (!prevRelease) return false; // oldest release — nothing to compare against
    const prev = prevRelease.containers.find((c) => c.containerName === container.containerName);
    if (!prev) return true; // new container not present in previous release
    return prev.containerVersion !== container.containerVersion;
  }

  get normalizedStatus(): string {
    const raw = this.siteReleases?.deploymentStatus;
    if (raw == null) return 'unknown';
    const s = String(raw).toLowerCase();
    if (s === '1' || s === 'healthy') return 'healthy';
    if (s === '2' || s === 'reportingfailure') return 'failure';
    if (s === '3' || s === 'containermismatch') return 'mismatch';
    return 'unknown';
  }

  get isVersionPending(): boolean {
    if (!this.siteReleases?.currentRelease || !this.siteReleases?.desiredRelease) return false;
    return this.siteReleases.currentRelease.id !== this.siteReleases.desiredRelease.id;
  }

  get isIotEdgeStale(): boolean {
    if (!this.siteReleases) return false;
    if (!this.siteReleases.deploymentHealthCheckedAt) return false;
    const checkedAt = this.parseUtcDate(this.siteReleases.deploymentHealthCheckedAt);
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return checkedAt < oneHourAgo;
  }

  get deploymentStatusLabel(): string {
    if (this.normalizedStatus === 'healthy' && this.isVersionPending) return 'Update Pending';
    switch (this.normalizedStatus) {
      case 'healthy':
        return 'OK';
      case 'failure':
        return 'Deployment Failure';
      case 'mismatch':
        return 'Version Mismatch';
      default:
        return 'Unknown';
    }
  }

  get deploymentStatusIcon(): string {
    if (this.normalizedStatus === 'healthy' && this.isVersionPending) return 'update';
    switch (this.normalizedStatus) {
      case 'healthy':
        return 'check_circle';
      case 'failure':
        return 'error';
      case 'mismatch':
        return 'warning';
      default:
        return 'help_outline';
    }
  }

  get deploymentStatusClass(): string {
    if (this.isIotEdgeStale && this.normalizedStatus === 'healthy') return 'status-outdated';
    if (this.normalizedStatus === 'healthy' && this.isVersionPending) return 'status-mismatch';
    switch (this.normalizedStatus) {
      case 'healthy':
        return 'status-healthy';
      case 'failure':
        return 'status-failure';
      case 'mismatch':
        return 'status-mismatch';
      default:
        return 'status-unknown';
    }
  }

  // On-prem reported status
  get onPremStatus(): string {
    if (!this.siteReleases) return 'unknown';
    if (this.siteReleases.reportedContainers == null) return 'never-reported';
    if (this.siteReleases.reportedMissingContainers != null && this.siteReleases.reportedMissingContainers.length > 0) {
      return 'mismatch';
    }
    return 'healthy';
  }

  get onPremStatusLabel(): string {
    switch (this.onPremStatus) {
      case 'healthy':
        return 'OK';
      case 'mismatch':
        return 'Version Mismatch';
      case 'never-reported':
        return 'Never Reported';
      default:
        return 'Unknown';
    }
  }

  get onPremStatusIcon(): string {
    switch (this.onPremStatus) {
      case 'healthy':
        return 'check_circle';
      case 'mismatch':
        return 'warning';
      case 'never-reported':
        return 'help_outline';
      default:
        return 'help_outline';
    }
  }

  get onPremStatusClass(): string {
    if (this.isOnPremStale && this.onPremStatus === 'healthy') return 'status-outdated';
    switch (this.onPremStatus) {
      case 'healthy':
        return 'status-healthy';
      case 'mismatch':
        return 'status-mismatch';
      default:
        return 'status-unknown';
    }
  }

  private parseUtcDate(isoString: string): number {
    // Server timestamps may lack a timezone suffix — append Z to force UTC parsing
    const s = /[Z+\-]\d{2}:?\d{2}$|Z$/.test(isoString) ? isoString : isoString + 'Z';
    return new Date(s).getTime();
  }

  get isOnPremStale(): boolean {
    if (!this.siteReleases) return false;
    if (!this.siteReleases.reportedContainersAt) {
      return this.siteReleases.desiredRelease != null;
    }
    const reportedAt = this.parseUtcDate(this.siteReleases.reportedContainersAt);
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return reportedAt < oneHourAgo;
  }

  getContainerImage(container: ContainerInfo): string {
    if (container.containerRegistry) {
      return `${container.containerRegistry}/${container.containerName}:${container.containerVersion}`;
    }
    return `${container.containerName}:${container.containerVersion}`;
  }

  get containerComparisonRows(): ContainerComparisonRow[] {
    if (!this.siteReleases) return [];

    const desiredContainers = this.siteReleases.desiredRelease?.containers || [];
    const reportedContainers = this.siteReleases.reportedContainers || [];
    const iotEdgeContainers = this.siteReleases.currentRelease?.containers || [];

    const containerNames = new Set<string>();
    desiredContainers.forEach((c) => containerNames.add(c.containerName));
    reportedContainers.forEach((c) => containerNames.add(c.containerName));
    iotEdgeContainers.forEach((c) => containerNames.add(c.containerName));

    const rows: ContainerComparisonRow[] = [];
    containerNames.forEach((name) => {
      const desired = desiredContainers.find((c) => c.containerName === name);
      const onPrem = reportedContainers.find((c) => c.containerName === name);
      const iotEdge = iotEdgeContainers.find((c) => c.containerName === name);

      rows.push({
        containerName: name,
        desiredVersion: desired?.containerVersion || null,
        onPremVersion: onPrem?.containerVersion || null,
        iotEdgeVersion: iotEdge?.containerVersion || null,
        onPremMismatch: desired != null && onPrem != null && desired.containerVersion !== onPrem.containerVersion,
        iotEdgeMismatch: desired != null && iotEdge != null && desired.containerVersion !== iotEdge.containerVersion,
      });
    });

    return rows.sort((a, b) => a.containerName.localeCompare(b.containerName));
  }
}

export interface ContainerComparisonRow {
  containerName: string;
  desiredVersion: string | null;
  onPremVersion: string | null;
  iotEdgeVersion: string | null;
  onPremMismatch: boolean;
  iotEdgeMismatch: boolean;
}

@Component({
  selector: 'sitedetails-set-available-releases-dialog',
  template: `
    <mat-card appearance="outlined" style="min-width: 420px">
      <h3 style="margin-top: 0">Set Available Releases</h3>
      <p style="font-size: 13px; color: #424242">
        Select the newest release to make available on this site. All earlier releases are included automatically.
      </p>
      <mat-form-field style="width: 100%">
        <mat-label>Newest Available Release</mat-label>
        <mat-select [(ngModel)]="selectedId">
          <mat-option [value]="null">-- None (clear all) --</mat-option>
          <mat-option *ngFor="let r of data.allReleases" [value]="r.id">
            {{ r.version }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      <p *ngIf="selectedId" style="color: #1565c0; font-size: 12px; font-style: italic; margin: 4px 0 16px">
        This and all earlier releases will be included ({{ selectionCount }} total).
      </p>
      <div style="display: flex; gap: 8px; justify-content: flex-end; margin-top: 8px">
        <button mat-raised-button (click)="dialogRef.close(undefined)">Cancel</button>
        <button mat-raised-button color="primary" (click)="dialogRef.close({ newestId: selectedId })">Save</button>
      </div>
    </mat-card>
  `,
  standalone: false,
})
export class SetAvailableReleaseDialogComponent {
  selectedId: string | null;

  constructor(
    public dialogRef: MatDialogRef<SetAvailableReleaseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { allReleases: IotEdgeReleaseResponse[]; currentNewestId: string | null }
  ) {
    this.selectedId = data.currentNewestId;
  }

  get selectionCount(): number {
    if (!this.selectedId) return 0;
    const idx = this.data.allReleases.findIndex((r) => r.id === this.selectedId);
    return idx >= 0 ? this.data.allReleases.length - idx : 0;
  }
}
